import React, { useState } from "react";
import { Animated, Pressable, StyleSheet, Text, View } from "react-native";

export interface SharedCareCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  expandedContent?: string[] | undefined;
  variant?: "desktop" | "mobile";
}

const CARD_WIDTH = 409.67;
const CARD_HEIGHT = 274;

export default function SharedCareCard({
  title,
  description,
  icon,
  expandedContent,
  variant = "desktop",
}: SharedCareCardProps) {
  const isMobile = variant === "mobile";
  const [isFlipped, setIsFlipped] = useState(false);
  const flipAnimation = React.useRef(new Animated.Value(0)).current;
  const flipCard = () => {
    if (isFlipped) {
      Animated.parallel([
        Animated.timing(flipAnimation, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(flipAnimation, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
    setIsFlipped(!isFlipped);
  };

  const frontOpacity = flipAnimation.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0, 0],
  });

  const backOpacity = flipAnimation.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  const frontScale = flipAnimation.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0.8, 0.8],
  });

  const backScale = flipAnimation.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.8, 0.8, 1],
  });

  const frontAnimatedStyle = {
    opacity: frontOpacity,
    transform: [{ scale: frontScale }],
  };

  const backAnimatedStyle = {
    opacity: backOpacity,
    transform: [{ scale: backScale }],
  };

  return (
    <View style={[styles.cardWrapper, isMobile && styles.cardWrapperMobile]}>
      <View style={styles.cardContainer}>
        {/* Front of card */}
        <Animated.View
          style={[styles.card, styles.cardFront, frontAnimatedStyle]}
          pointerEvents={isFlipped ? "none" : "auto"}
        >
          <View style={styles.frontContent}>
            <View style={styles.icon}>{icon}</View>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.description} numberOfLines={3}>
              {description}
            </Text>
            {expandedContent && expandedContent.length > 0 && (
              <Pressable onPress={flipCard} style={styles.seeMoreButton}>
                <Text style={styles.seeMoreText}>SEE MORE</Text>
              </Pressable>
            )}
          </View>
        </Animated.View>

        {/* Back of card */}
        {expandedContent && expandedContent.length > 0 && (
          <Animated.View
            style={[styles.card, styles.cardBack, backAnimatedStyle]}
            pointerEvents={isFlipped ? "auto" : "none"}
          >
            <View style={styles.backContent}>
              <View style={styles.backTextContainer}>
                {expandedContent.map((item, index) => (
                  <Text key={index} style={styles.expandedItem}>
                    {item}
                  </Text>
                ))}
              </View>
              <Pressable onPress={flipCard} style={styles.closeButton}>
                <Text style={styles.closeText}>CLOSE</Text>
              </Pressable>
            </View>
          </Animated.View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  cardWrapper: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
  },
  cardWrapperMobile: {
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
  },
  cardContainer: {
    width: "100%",
    height: "100%",
  },
  card: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundColor: "#f6f7f9",
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    borderColor: "#d8dbe1",
  },
  cardFront: {
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardBack: {
    justifyContent: "space-between",
    alignItems: "center",
  },
  frontContent: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
  },
  icon: {
    width: 60,
    height: 60,
    marginBottom: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "900",
    color: "#2b2b2f",
    marginBottom: 4,
    textAlign: "center",
  },
  description: {
    fontSize: 16,
    color: "#2b2b2f",
    lineHeight: 20,
    textAlign: "center",
    marginBottom: 4,
    flexShrink: 1,
    opacity: 0.5,
  },
  seeMoreButton: {
    paddingVertical: 6,
    alignSelf: "center",
  },
  seeMoreText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#3a6fb1",
  },
  backContent: {
    flex: 1,
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  backTextContainer: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 8,
  },
  expandedItem: {
    fontSize: 14,
    color: "#2b2b2f",
    lineHeight: 20,
    marginBottom: 8,
    textAlign: "center",
    width: "100%",
    opacity: 0.6,
  },
  closeButton: {
    paddingVertical: 6,
    alignSelf: "center",
    marginTop: 8,
  },
  closeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#3a6fb1",
  },
});
